import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  Card,
  CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShareIcon from '@mui/icons-material/Share';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import HeightIcon from '@mui/icons-material/Height';
import toast from 'react-hot-toast';

import DeliveryCard from '../components/DeliveryCard';
import { getAnalysis, deleteAnalysis } from '../services/api';

import TrajectoryPlot from '../components/TrajectoryPlot';
import PitchMap from '../components/PitchMap';
import Speedometer from '../components/Speedometer';
import SwingVisualization from '../components/SwingVisualization';
import SpinVisualization from '../components/SpinVisualization';
import MultiDeliveryPitchMap from '../components/MultiDeliveryPitchMap';
import SpeedProfileChart from '../components/SpeedProfileChart';

const Results = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedDeliveryIndex, setSelectedDeliveryIndex] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchAnalysis();
  }, [analysisId]);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      const data = await getAnalysis(analysisId);
      setAnalysis(data);
      setError(null);
      toast.success('Analysis loaded successfully');
    } catch (err) {
      console.error('Error fetching analysis:', err);
      setError('Failed to load analysis results');
      toast.error('Failed to load analysis results');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAnalysis(analysisId);
      toast.success('Analysis deleted successfully');
      navigate('/history');
    } catch (err) {
      console.error('Error deleting analysis:', err);
      toast.error('Failed to delete analysis');
    }
    setDeleteDialogOpen(false);
  };

  const handleDownload = () => {
    if (!analysis) return;
    
    const dataStr = JSON.stringify(analysis, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `cricket_analysis_${analysisId}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    toast.success('Analysis downloaded');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Cricket Analysis: ${analysis?.filename}`,
        text: `Check out this cricket ball analysis: ${analysis?.summary?.average_speed_kmh} km/h average speed`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getSwingDirectionText = (swingType) => {
    switch(swingType) {
      case 'inswing': return 'Inswing';
      case 'outswing': return 'Outswing';
      case 'straight': return 'Straight';
      default: return 'No Swing';
    }
  };

  const getDeliveryTypeColor = (type) => {
    switch(type) {
      case 'fast': return theme.palette.error.main;
      case 'fast_medium': return theme.palette.warning.main;
      case 'medium': return theme.palette.info.main;
      case 'spin': return theme.palette.success.main;
      case 'slow': return theme.palette.secondary.main;
      default: return theme.palette.text.secondary;
    }
  };

  const getDeliveryTypeLabel = (type) => {
    switch(type) {
      case 'fast': return 'Fast';
      case 'fast_medium': return 'Fast Medium';
      case 'medium': return 'Medium';
      case 'spin': return 'Spin';
      case 'slow': return 'Slow';
      default: return type;
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (error || !analysis) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/history')}>
              Back to History
            </Button>
          }
        >
          {error || 'Analysis not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/history')}>
          Back to History
        </Button>
      </Container>
    );
  }

  const { filename, timestamp, deliveries, summary, video_metadata, processing_time, calibration_used } = analysis;
  const selectedDelivery = deliveries[selectedDeliveryIndex];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Cricket Ball Analysis
            </Typography>
            <Typography color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccessTimeIcon fontSize="small" />
              {formatDate(timestamp)}
            </Typography>
            <Typography color="text.secondary">
              {filename}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <IconButton onClick={handleShare} color="primary" sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>
              <ShareIcon />
            </IconButton>
            <IconButton onClick={() => setDeleteDialogOpen(true)} color="error" sx={{ border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
              <DeleteIcon />
            </IconButton>
            <IconButton onClick={handleDownload} color="primary" sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>
              <DownloadIcon />
            </IconButton>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/history')} variant="outlined">
              Back
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Chip 
              label={`${deliveries.length} Delivery${deliveries.length > 1 ? 's' : ''}`} 
              color="primary"
              size="medium"
              sx={{ fontWeight: 600 }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeedIcon fontSize="small" />
              Processing Time: {processing_time}s
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Video: {video_metadata?.resolution || 'Unknown'} @ {video_metadata?.fps || 'Unknown'} FPS
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper
        sx={{
          mb: 4,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              py: 2,
              fontSize: '1rem',
              fontWeight: 500,
            },
            '& .Mui-selected': {
              color: theme.palette.primary.main,
            },
          }}
        >
          <Tab icon={<BarChartIcon />} label="Overview" />
          <Tab icon={<TimelineIcon />} label="Trajectory Analysis" />
          <Tab icon={<LocationOnIcon />} label="Deliveries" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Key Metrics */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 4,
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Performance Overview
              </Typography>
              <Divider sx={{ mb: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              
              <Grid container spacing={4}>
                {/* Speed Metrics */}
                <Grid item xs={6} md={3}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                  }}>
                    <Typography variant="h2" sx={{ color: theme.palette.primary.main }}>
                      {summary?.average_speed_kmh?.toFixed(1) || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg Speed (km/h)
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                  }}>
                    <Typography variant="h2" sx={{ color: theme.palette.secondary.main }}>
                      {summary?.max_speed_kmh?.toFixed(1) || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Max Speed (km/h)
                    </Typography>
                  </Card>
                </Grid>
                
                {/* Swing Distribution */}
                <Grid item xs={6} md={3}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.2)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                  }}>
                    <Typography variant="h2" sx={{ color: theme.palette.success.main }}>
                      {summary?.swing_distribution?.outswing || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Outswing Deliveries
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.2)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                  }}>
                    <Typography variant="h2" sx={{ color: theme.palette.info.main }}>
                      {summary?.swing_distribution?.inswing || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Inswing Deliveries
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Pitch Map for Multiple Deliveries */}
          {deliveries.length > 1 && (
            <Grid item xs={12} md={8}>
              <Paper
                sx={{
                  p: 4,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Pitch Impact Map
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Heatmap showing where balls pitched across all deliveries
                </Typography>
                <MultiDeliveryPitchMap deliveries={deliveries} />
              </Paper>
            </Grid>
          )}

          {/* Distribution Stats */}
          <Grid item xs={12} md={deliveries.length > 1 ? 4 : 12}>
            <Paper
              sx={{
                p: 4,
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                height: '100%',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Delivery Statistics
              </Typography>
              
              <Grid container spacing={3}>
                {/* Length Distribution */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Length Distribution
                  </Typography>
                  {summary?.length_distribution && Object.entries(summary.length_distribution).map(([category, count]) => (
                    <Box key={category} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" sx={{ minWidth: 120 }}>
                        {category.replace('_', ' ')}
                      </Typography>
                      <Box sx={{ flexGrow: 1, ml: 2 }}>
                        <Box
                          sx={{
                            height: 10,
                            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                            width: `${(count / deliveries.length) * 100}%`,
                            borderRadius: 5,
                            transition: 'width 0.5s ease',
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ ml: 2, minWidth: 40, fontWeight: 500 }}>
                        {count}
                      </Typography>
                    </Box>
                  ))}
                </Grid>

                {/* Line Distribution */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Line Distribution
                  </Typography>
                  {summary?.line_distribution && Object.entries(summary.line_distribution).map(([category, count]) => (
                    <Box key={category} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" sx={{ minWidth: 120 }}>
                        {category}
                      </Typography>
                      <Box sx={{ flexGrow: 1, ml: 2 }}>
                        <Box
                          sx={{
                            height: 10,
                            background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${alpha(theme.palette.secondary.main, 0.7)} 100%)`,
                            width: `${(count / deliveries.length) * 100}%`,
                            borderRadius: 5,
                            transition: 'width 0.5s ease',
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ ml: 2, minWidth: 40, fontWeight: 500 }}>
                        {count}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Trajectory Analysis Tab */}
      {/* Trajectory Analysis Tab */}
{activeTab === 1 && deliveries.length > 0 && (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    {/* Delivery Selector */}
    {deliveries.length > 1 && (
      <Paper sx={{ 
        p: 2, 
        mb: 2, 
        display: 'flex', 
        flexWrap: 'wrap', 
        alignItems: 'center',
        gap: 2,
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        borderRadius: 2,
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'primary.main' }}>
          Select Delivery:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {deliveries.map((delivery, index) => (
            <Chip
              key={index}
              label={`Delivery ${index + 1}`}
              onClick={() => setSelectedDeliveryIndex(index)}
              color={selectedDeliveryIndex === index ? "primary" : "default"}
              variant={selectedDeliveryIndex === index ? "filled" : "outlined"}
              sx={{ 
                fontWeight: 500,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[2],
                }
              }}
            />
          ))}
        </Box>
      </Paper>
    )}

    {/* Current Delivery Info */}
    <Paper sx={{ 
      p: 3, 
      display: 'grid', 
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
      gap: 3,
      alignItems: 'center',
      backgroundColor: alpha(theme.palette.background.paper, 0.7),
      backdropFilter: 'blur(10px)',
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      borderRadius: 2,
    }}>
      <Box sx={{ textAlign: 'center', p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Delivery Type
        </Typography>
        <Chip 
          label={getDeliveryTypeLabel(selectedDelivery.delivery_type)}
          sx={{ 
            backgroundColor: alpha(getDeliveryTypeColor(selectedDelivery.delivery_type), 0.15),
            color: getDeliveryTypeColor(selectedDelivery.delivery_type),
            fontWeight: 600,
            px: 2,
            py: 1,
            fontSize: '0.9rem',
            border: `1px solid ${alpha(getDeliveryTypeColor(selectedDelivery.delivery_type), 0.3)}`,
          }}
        />
      </Box>
      
      <Box sx={{ textAlign: 'center', p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Swing Analysis
        </Typography>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <TrendingFlatIcon 
            sx={{ 
              transform: selectedDelivery.metrics.swing_type === 'outswing' ? 'scaleX(-1)' : 'none',
              color: selectedDelivery.metrics.swing_type === 'straight' ? theme.palette.text.secondary : 
                     selectedDelivery.metrics.swing_type === 'outswing' ? theme.palette.success.main : 
                     theme.palette.info.main,
              fontSize: '1.5rem'
            }} 
          />
          <Box>
            <Box sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {Math.abs(selectedDelivery.metrics.swing_angle_degrees).toFixed(1)}°
            </Box>
            <Box sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
              {getSwingDirectionText(selectedDelivery.metrics.swing_type)}
            </Box>
          </Box>
        </Typography>
      </Box>
      
      <Box sx={{ textAlign: 'center', p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Spin Rate
        </Typography>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <RotateRightIcon sx={{ color: theme.palette.secondary.main, fontSize: '1.5rem' }} />
          <Box>
            <Box sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {selectedDelivery.metrics.spin_rpm ? `${selectedDelivery.metrics.spin_rpm.toFixed(0)}` : 'N/A'}
            </Box>
            <Box sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
              RPM
            </Box>
          </Box>
        </Typography>
      </Box>
      
      <Box sx={{ textAlign: 'center', p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Bounce Height
        </Typography>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <HeightIcon sx={{ color: theme.palette.warning.main, fontSize: '1.5rem' }} />
          <Box>
            <Box sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {selectedDelivery.pitch_impact.bounce_height.toFixed(2)}
            </Box>
            <Box sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
              meters
            </Box>
          </Box>
        </Typography>
      </Box>
    </Paper>

    {/* Key Metrics Row - Fixed Grid Layout */}
    <Box sx={{ 
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(3, 1fr)' },
      gap: 3,
      mb: 3,
    }}>
      {/* Speedometer */}
      <Paper sx={{ 
        p: 3, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        borderRadius: 2,
        minHeight: { xs: 300, md: 350 },
      }}>
        <Speedometer 
          speed={selectedDelivery.metrics.speed_kmh}
          label="Ball Speed"
          maxSpeed={160}
          size="large"
          showComparison={deliveries.length > 1}
          comparisonValue={summary?.average_speed_kmh}
        />
      </Paper>
      
      {/* Swing Visualization */}
      <Paper sx={{ 
        p: 3, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
        borderRadius: 2,
        minHeight: { xs: 300, md: 350 },
      }}>
        <SwingVisualization 
          swingType={selectedDelivery.metrics.swing_type}
          swingAngle={selectedDelivery.metrics.swing_angle_degrees}
          swingMagnitude={selectedDelivery.metrics.swing_magnitude}
          size="large"
        />
      </Paper>
      
      {/* Spin Visualization */}
      <Paper sx={{ 
        p: 3, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
        borderRadius: 2,
        minHeight: { xs: 300, md: 350 },
      }}>
        <SpinVisualization 
          spinRpm={selectedDelivery.metrics.spin_rpm}
          deliveryType={selectedDelivery.delivery_type}
          size="large"
        />
      </Paper>
    </Box>

    {/* Trajectory and Speed Profile - Fixed Grid Layout */}
    <Box sx={{ 
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
      gap: 3,
      mb: 3,
    }}>
      {/* Trajectory Plot */}
      <Paper sx={{ 
        p: 3, 
        height: '100%',
        minHeight: 400,
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Ball Trajectory
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Flight path showing movement in 2D space (distance vs height)
        </Typography>
        <Box sx={{ 
          flex: 1, 
          position: 'relative',
          minHeight: 300,
        }}>
          <TrajectoryPlot 
            trajectory={selectedDelivery.trajectory}
            pitchImpact={selectedDelivery.pitch_impact}
            showPath={true}
            showImpactPoint={true}
            showReleasePoint={true}
            width="100%"
            height="100%"
          />
        </Box>
      </Paper>
      
      {/* Speed Profile */}
      <Paper sx={{ 
        p: 3, 
        height: '100%',
        minHeight: 400,
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Speed Profile
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Speed variation along the trajectory
        </Typography>
        <Box sx={{ 
          flex: 1, 
          position: 'relative',
          minHeight: 300,
        }}>
          <SpeedProfileChart 
            trajectory={selectedDelivery.trajectory}
            speedProfile={selectedDelivery.visualization.speed_profile}
            fps={video_metadata?.fps}
            height={300}
          />
        </Box>
      </Paper>
    </Box>

    {/* Pitch Map Section */}
    <Paper sx={{ 
      p: 3,
      backgroundColor: alpha(theme.palette.background.paper, 0.7),
      backdropFilter: 'blur(10px)',
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      borderRadius: 2,
    }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Pitch Impact Analysis
      </Typography>
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
        gap: 3,
      }}>
        {/* Pitch Map */}
        <Box sx={{ 
          height: 350,
          backgroundColor: alpha(theme.palette.background.default, 0.5),
          borderRadius: 1,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <PitchMap 
            pitchImpact={selectedDelivery.pitch_impact}
            showDimensions={true}
            showLegend={true}
            interactive={true}
            width="100%"
            height="100%"
          />
        </Box>
        
        {/* Impact Details */}
        <Box sx={{ 
          p: { xs: 2, md: 0 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            Impact Details
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Length Category
              </Typography>
              <Chip 
                label={selectedDelivery.pitch_impact.length_category.replace('_', ' ')}
                size="medium"
                sx={{ 
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  textTransform: 'capitalize',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                }}
              />
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Line Category
              </Typography>
              <Chip 
                label={selectedDelivery.pitch_impact.line_category}
                size="medium"
                sx={{ 
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  textTransform: 'capitalize',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                }}
              />
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Distance from Crease
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                {selectedDelivery.pitch_impact.x.toFixed(2)} m
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Lateral Position
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.secondary.main }}>
                {selectedDelivery.pitch_impact.y.toFixed(2)} m
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Bounce Height
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                {selectedDelivery.pitch_impact.bounce_height.toFixed(2)} m
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>

    {/* Additional Metrics - Fixed Grid Layout */}
    <Box sx={{ 
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
      gap: 3,
    }}>
      {/* Release & Impact Speeds */}
      <Paper sx={{ 
        p: 3,
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        borderRadius: 2,
        height: '100%',
      }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SpeedIcon fontSize="small" />
          Release & Impact Speeds
        </Typography>
        <Divider sx={{ mb: 3, borderColor: alpha(theme.palette.divider, 0.2) }} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Release Speed
              </Typography>
              <Typography variant="h4" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                {selectedDelivery.metrics.release_speed_kmh.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                km/h
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Impact Speed
              </Typography>
              <Typography variant="h4" sx={{ color: theme.palette.secondary.main, fontWeight: 700 }}>
                {selectedDelivery.metrics.impact_speed_kmh.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                km/h
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Speed Loss
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flexGrow: 1, position: 'relative', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                  <Box sx={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '100%',
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  }} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                  {((selectedDelivery.metrics.release_speed_kmh - selectedDelivery.metrics.impact_speed_kmh) / selectedDelivery.metrics.release_speed_kmh * 100).toFixed(1)}%
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {(selectedDelivery.metrics.release_speed_kmh - selectedDelivery.metrics.impact_speed_kmh).toFixed(1)} km/h loss
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Flight Characteristics */}
      <Paper sx={{ 
        p: 3,
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
        borderRadius: 2,
        height: '100%',
      }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon fontSize="small" />
          Flight Characteristics
        </Typography>
        <Divider sx={{ mb: 3, borderColor: alpha(theme.palette.divider, 0.2) }} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Time of Flight
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                {(selectedDelivery.trajectory[selectedDelivery.trajectory.length - 1]?.timestamp || 0).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                seconds
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Max Height
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                {selectedDelivery.visualization.height_metrics.max_height.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                meters
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Release Height
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {selectedDelivery.visualization.height_metrics.release_height.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                meters
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Swing Magnitude
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {(selectedDelivery.metrics.swing_magnitude || 0).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                meters
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Delivery Stats */}
      <Paper sx={{ 
        p: 3,
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
        borderRadius: 2,
        height: '100%',
      }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon fontSize="small" />
          Delivery Stats
        </Typography>
        <Divider sx={{ mb: 3, borderColor: alpha(theme.palette.divider, 0.2) }} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Frames
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {selectedDelivery.trajectory.length}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Timestamp
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {selectedDelivery.timestamp_in_video.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                seconds
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Trajectory Points
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedDelivery.visualization.trajectory_stats.total_points} points
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Horizontal Range
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedDelivery.visualization.trajectory_stats.horizontal_range.toFixed(2)} meters
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Vertical Range
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedDelivery.visualization.trajectory_stats.vertical_range.toFixed(2)} meters
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  </Box>
)}

      {/* Individual Deliveries Tab */}
      {activeTab === 2 && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
            Individual Delivery Analysis
          </Typography>
          
          {deliveries.map((delivery, index) => (
            <DeliveryCard
              key={index}
              delivery={delivery}
              index={index}
              onSelect={() => {
                setSelectedDeliveryIndex(index);
                setActiveTab(1);
              }}
            />
          ))}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Analysis</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this analysis? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Results;